import { useNavigate, useSearch } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { captureAutomationEvent } from '@apps/web/components/automations/automation_analytics';
import { coordinatorAutomationProperties } from '@apps/web/components/automations/customer/automation_analytics';
import { AutomationDetailHeader } from '@apps/web/components/automations/customer/automation_detail_header';
import { AutomationDialog } from '@apps/web/components/automations/customer/automation_dialog';
import { AutomationRunsTable } from '@apps/web/components/automations/customer/automation_runs_table';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { toast } from '@apps/web/lib/app_notifications';
import { EVENTS } from '@apps/web/lib/events';
import {
    useCoordinatorAutomation,
    useCoordinatorAutomationActions,
    useOrganization,
    useRevalidateCoordinatorAutomations,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { ApiError } from '@libraries/typescript/api_client';

const ROUTINE_DETAIL_ROUTE_ID = '/_app/(main)/routines/$routineId';

export function AutomationDetailPage({
    automationId,
    organizationId,
}: {
    readonly automationId: string;
    readonly organizationId: string;
}): ReactElement {
    const navigate = useNavigate();
    const posthog = usePostHog();
    const { run: openRunId } = useSearch({
        from: ROUTINE_DETAIL_ROUTE_ID,
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [isTogglingEnabled, setIsTogglingEnabled] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { deleteAutomation, updateAutomation } =
        useCoordinatorAutomationActions();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'coordinator-automation-detail',
    });
    const {
        data: automation,
        error,
        mutate,
    } = useCoordinatorAutomation(
        organizationId,
        automationId,
        { reason: 'coordinator-automation-detail' },
        // Chat-tool edits publish no event, so poll.
        { refreshInterval: 5000 },
    );
    const revalidateAutomations = useRevalidateCoordinatorAutomations();

    const viewedRef = useRef<string | null>(null);
    const openedRunRef = useRef<string | null>(null);

    useEffect(() => {
        if (automation === undefined || viewedRef.current === automation.id) {
            return;
        }
        viewedRef.current = automation.id;
        captureAutomationEvent(
            posthog,
            EVENTS.AUTOMATION_VIEWED,
            coordinatorAutomationProperties({ automation, organizationId }),
        );
    }, [automation, organizationId, posthog]);

    useEffect(() => {
        if (
            automation === undefined ||
            openRunId === undefined ||
            openedRunRef.current === openRunId
        ) {
            return;
        }
        openedRunRef.current = openRunId;
        captureAutomationEvent(posthog, EVENTS.AUTOMATION_RUN_OPENED, {
            ...coordinatorAutomationProperties({ automation, organizationId }),
            phoebe_agent_run_id: openRunId,
        });
    }, [automation, openRunId, organizationId, posthog]);

    if (automation === undefined) {
        const isMissing = error instanceof ApiError && error.status === 404;
        return (
            <div className="mx-auto w-full max-w-4xl min-w-0 space-y-8 px-6 py-10 lg:px-10">
                {isMissing ? (
                    <p className="text-muted-foreground text-sm">
                        This routine is no longer available.
                    </p>
                ) : error ? (
                    <p role="alert" className="text-destructive text-sm">
                        Couldn’t load this routine.
                    </p>
                ) : (
                    <Skeleton className="h-40 w-full" />
                )}
            </div>
        );
    }

    const analyticsProperties = coordinatorAutomationProperties({
        automation,
        organizationId,
    });
    const timezone = organization?.timezone ?? automation.timezone;
    const nextEnabled = !automation.enabled;

    async function toggleEnabled(): Promise<void> {
        setIsTogglingEnabled(true);
        try {
            await updateAutomation(organizationId, automationId, {
                enabled: nextEnabled,
            });
            await mutate();
            await revalidateAutomations();
            if (!nextEnabled) {
                captureAutomationEvent(
                    posthog,
                    EVENTS.AUTOMATION_PAUSED,
                    analyticsProperties,
                );
            }
            toast.success(nextEnabled ? 'Routine resumed' : 'Routine paused', {
                persistNotification: false,
            });
        } catch {
            toast.error('Couldn’t update this routine.', {
                persistNotification: false,
            });
        } finally {
            setIsTogglingEnabled(false);
        }
    }

    async function confirmDelete(): Promise<void> {
        setIsDeleting(true);
        try {
            await deleteAutomation(organizationId, automationId);
            await revalidateAutomations();
            setIsConfirmingDelete(false);
            captureAutomationEvent(
                posthog,
                EVENTS.AUTOMATION_DELETED,
                analyticsProperties,
            );
            toast.success('Routine deleted', {
                persistNotification: false,
            });
            await navigate({ to: '/routines' });
        } catch {
            toast.error('Couldn’t delete this routine.', {
                persistNotification: false,
            });
        } finally {
            setIsDeleting(false);
        }
    }

    return (
        <div className="mx-auto w-full max-w-4xl min-w-0 space-y-8 px-6 py-10 lg:px-10">
            <AutomationDetailHeader
                automation={automation}
                isTogglingEnabled={isTogglingEnabled}
                onDelete={() => setIsConfirmingDelete(true)}
                onEdit={() => setIsEditing(true)}
                onToggleEnabled={() => void toggleEnabled()}
                timezone={timezone}
            />
            <section
                aria-labelledby="automation-instructions-heading"
                className="space-y-2"
            >
                <h2
                    id="automation-instructions-heading"
                    className="text-foreground text-[15px] leading-[22px] font-semibold"
                >
                    Instructions
                </h2>
                <p className="text-muted-foreground max-w-[66ch] text-sm whitespace-pre-wrap">
                    {automation.prompt}
                </p>
            </section>
            <AutomationRunsTable
                automationId={automationId}
                failedCount={automation.failed_count}
                organizationId={organizationId}
                runCount={automation.run_count}
                timezone={timezone}
            />
            <AutomationDialog
                automation={automation}
                open={isEditing}
                onOpenChange={setIsEditing}
                organizationId={organizationId}
                onSaved={async () => {
                    await mutate();
                    await revalidateAutomations();
                }}
            />
            <Dialog
                open={isConfirmingDelete}
                onOpenChange={setIsConfirmingDelete}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete “{automation.name}”?</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground text-sm">
                        Its past runs stay in Chats.
                    </p>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsConfirmingDelete(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            disabled={isDeleting}
                            onClick={() => void confirmDelete()}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
